import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PostService, Post } from '../../services/post.service';
import { WebsiteService, SupportedLanguage, LanguageOption, Domain } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type SortColumn = 'title' | 'date' | null;
type SortDirection = 'asc' | 'desc' | null;

interface CategoryFilter {
  id: string;
  name: string;
}

@Component({
  selector: 'app-posts',
  templateUrl: './posts.component.html',
  styleUrls: ['./posts.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NotificationComponent,
    ConfirmModalComponent
  ]
})
export class PostsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  posts: Post[] = [];
  filteredPosts: Post[] = [];
  categories: CategoryFilter[] = [];
  domains: Domain[] = [];
  loading = false;

  filterText = '';
  filterCategoryId: string | null = null; // null = all, 'uncategorized' = no category, id = category
  filterDomain: string | null = null; // null = all domains, otherwise domain name

  sortColumn: SortColumn = null;
  sortDirection: SortDirection = null;

  currentLocale: SupportedLanguage = 'nl';
  availableLanguages: LanguageOption[] = [];

  showDeletePostModal = false;
  postToDelete: Post | null = null;
  deleteAllLanguages = true; // Standaard: verwijder alle taalversies

  showNewCategoryInput = false;
  newCategoryName = '';

  constructor(
    private router: Router,
    private postService: PostService,
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    this.loading = true;

    // Subscriptions voor toekomstige wijzigingen (niet await-en)
    this.websiteService.currentDomain$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (domain) => {
        if (domain) {
          // Parallel laden
          await Promise.all([
            this.loadCategories(domain),
            this.loadLanguagesForDomain(domain)
          ]);
        }
        this.loadPosts();
      });

    this.websiteService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        this.currentLocale = lang;
        this.loadPosts();
      });

    // Initiële load: alles parallel
    const currentDomain = this.websiteService.getCurrentDomain();
    const initPromises: Promise<void>[] = [this.loadDomains()];
    if (currentDomain) {
      initPromises.push(this.loadCategories(currentDomain));
      initPromises.push(this.loadLanguagesForDomain(currentDomain));
    }
    await Promise.all(initPromises);
    this.loadPosts();
  }

  async loadDomains() {
    this.domains = await this.websiteService.getDomains();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadLanguagesForDomain(domain: string) {
    const settings = await this.websiteService.getWebsiteSettings(domain);
    if (settings?.availableLanguages?.length) {
      this.availableLanguages = settings.availableLanguages;
    } else {
      this.availableLanguages = [
        { code: 'nl', name: 'Nederlands' },
        { code: 'en', name: 'English' }
      ];
    }
  }

  async loadCategories(domain: string) {
    const list = await this.websiteService.getCategories(domain);
    this.categories = list.map((c) => ({ id: c.id, name: c.name }));
  }

  async loadPosts() {
    this.loading = true;
    try {
      if (this.filterDomain) {
        // Filter op één domein + huidige taal + categorie
        const categoryParam =
          this.filterCategoryId === null ? undefined : this.filterCategoryId;
        this.posts = await this.postService.getPosts(this.filterDomain, this.currentLocale, categoryParam);
      } else {
        // Alle domeinen: posts per domein ophalen voor huidige taal, dedupliceren
        if (this.domains.length === 0) {
          this.domains = await this.websiteService.getDomains();
        }
        const byId = new Map<string, Post>();
        for (const d of this.domains) {
          const list = await this.postService.getPosts(d.name, this.currentLocale, undefined);
          list.forEach((p) => byId.set(p.id, p));
        }
        const allPosts = Array.from(byId.values());
        allPosts.sort((a, b) =>
          ((b.updatedAt || b.createdAt)?.getTime() ?? 0) - ((a.updatedAt || a.createdAt)?.getTime() ?? 0)
        );
        this.posts = allPosts;
      }
      this.applyFiltersAndSort();
    } catch (error) {
      console.error('Error loading posts:', error);
      this.notificationService.show({
        message: 'Fout bij laden van berichten',
        type: 'error'
      });
    } finally {
      this.loading = false;
    }
  }

  applyFiltersAndSort() {
    let filtered = [...this.posts];

    if (this.filterText.trim()) {
      const q = this.filterText.toLowerCase();
      filtered = filtered.filter((p) => {
        const title = this.getPostTitle(p).toLowerCase();
        const slug = (p.slug || '').toLowerCase();
        return title.includes(q) || slug.includes(q);
      });
    }

    if (this.sortColumn && this.sortDirection) {
      filtered.sort((a, b) => {
        if (this.sortColumn === 'title') {
          const va = this.getPostTitle(a).toLowerCase();
          const vb = this.getPostTitle(b).toLowerCase();
          const cmp = va.localeCompare(vb);
          return this.sortDirection === 'asc' ? cmp : -cmp;
        }
        if (this.sortColumn === 'date') {
          const da = (a.updatedAt || a.createdAt)?.getTime() ?? 0;
          const db = (b.updatedAt || b.createdAt)?.getTime() ?? 0;
          return this.sortDirection === 'asc' ? da - db : db - da;
        }
        return 0;
      });
    }

    this.filteredPosts = filtered;
  }

  onFilterChange() {
    this.applyFiltersAndSort();
  }

  onCategoryFilterChange() {
    this.loadPosts();
  }

  async onDomainFilterChange() {
    if (this.filterDomain) {
      await this.loadCategories(this.filterDomain);
    }
    this.loadPosts();
  }

  /**
   * Wissel van taal – herlaad de posts lijst.
   */
  setCurrentLanguage(code: string) {
    this.currentLocale = code as SupportedLanguage;
    this.websiteService.setCurrentLanguage(code as SupportedLanguage);
    this.loadPosts();
  }

  cancelNewCategory() {
    this.showNewCategoryInput = false;
    this.newCategoryName = '';
  }

  async addCategory() {
    const domain = this.filterDomain || this.websiteService.getCurrentDomain();
    if (!domain) {
      this.notificationService.show({ message: 'Geen domein geselecteerd', type: 'error' });
      return;
    }
    const name = this.newCategoryName.trim();
    if (!name) {
      this.notificationService.show({ message: 'Voer een categorienaam in', type: 'error' });
      return;
    }
    const bestaand = this.categories.find((c) => c.name.toLowerCase() === name.toLowerCase());
    if (bestaand) {
      this.filterCategoryId = bestaand.id;
      this.cancelNewCategory();
      this.loadPosts();
      this.notificationService.show({ message: 'Categorie bestaat al, filter ingesteld', type: 'success' });
      return;
    }
    try {
      const id = await this.websiteService.createCategory(domain, name);
      await this.loadCategories(domain);
      this.filterCategoryId = id;
      this.cancelNewCategory();
      this.loadPosts();
      this.notificationService.show({ message: 'Categorie toegevoegd', type: 'success' });
    } catch (e) {
      console.error('Categorie toevoegen mislukt:', e);
      this.notificationService.show({
        message: e instanceof Error ? e.message : 'Categorie toevoegen mislukt',
        type: 'error'
      });
    }
  }

  onSort(column: SortColumn) {
    if (this.sortColumn === column) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      } else {
        this.sortDirection = 'asc';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFiltersAndSort();
  }

  getSortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) {
      return 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4';
    }
    return this.sortDirection === 'asc'
      ? 'M5 15l7-7 7 7'
      : 'M19 9l-7 7-7-7';
  }

  /**
   * Post.title is nu een string (niet meer een Record<lang, string>).
   */
  getPostTitle(post: Post): string {
    return post.title || 'Untitled';
  }

  getCategoryName(categoryId?: string | null): string {
    if (!categoryId) return 'Ongecategoriseerd';
    const c = this.categories.find((x) => x.id === categoryId);
    return c?.name ?? 'Ongecategoriseerd';
  }

  getCategoryNames(post: Post): string {
    const ids = post.categoryIds ?? [];
    if (ids.length === 0) return 'Ongecategoriseerd';
    return ids.map((id) => this.categories.find((x) => x.id === id)?.name ?? id).join(', ');
  }

  getPostDate(post: Post): Date | undefined {
    return post.updatedAt || post.createdAt;
  }

  getPostDomainsDisplay(post: Post): string {
    if (post.domains?.length) return post.domains.join(', ');
    return post.domain || '-';
  }

  getLanguageName(code: string): string {
    const names: Record<string, string> = {
      en: 'English', nl: 'Nederlands', fr: 'Français', de: 'Deutsch', es: 'Español', it: 'Italiano'
    };
    return names[code] || this.availableLanguages.find(l => l.code === code)?.name || code;
  }

  navigateToNew() {
    this.router.navigate(['/posts/edit/new']);
  }

  navigateToImport() {
    this.router.navigate(['/posts/import']);
  }

  navigateToCategories() {
    this.router.navigate(['/posts/categories']);
  }

  navigateToEdit(post: Post) {
    if (post.id) {
      this.router.navigate(['/posts/edit', post.id]);
    }
  }

  openDeleteModal(post: Post) {
    this.postToDelete = post;
    this.deleteAllLanguages = true;
    this.showDeletePostModal = true;
  }

  closeDeleteModal() {
    this.showDeletePostModal = false;
    this.postToDelete = null;
  }

  /**
   * Verwijder de post – standaard alle taalversies.
   */
  async deletePost() {
    if (!this.postToDelete?.id) return;

    try {
      if (this.deleteAllLanguages) {
        // Verwijder alle taalversies (base + linked docs)
        await this.postService.deleteAllLanguageVersions(
          this.postToDelete.id,
          this.postToDelete.basePostId
        );
        this.notificationService.show({
          message: 'Bericht en alle vertalingen verwijderd',
          type: 'success'
        });
      } else {
        // Verwijder alleen deze taalversie
        await this.postService.deletePost(this.postToDelete.id);
        this.notificationService.show({
          message: 'Taalversie verwijderd',
          type: 'success'
        });
      }
      this.closeDeleteModal();
      this.loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      this.notificationService.show({
        message: 'Fout bij verwijderen van bericht',
        type: 'error'
      });
    }
  }
}
